import React, { useState } from 'react';
import { Shop, AppSettings } from '../App';
import { PlusIcon, TrashIcon } from 'lucide-react';
interface SettingsProps {
  settings: AppSettings;
  setSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
  shops: Shop[];
  setShops: React.Dispatch<React.SetStateAction<Shop[]>>;
  onAddShop: () => void;
  onDeleteShop: (id: number) => void;
}
export const Settings: React.FC<SettingsProps> = ({
  settings,
  setSettings,
  shops,
  setShops,
  onAddShop,
  onDeleteShop
}) => {
  const [editingShopId, setEditingShopId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editCharges, setEditCharges] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const handleCostPerUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setSettings({
      ...settings,
      costPerUnit: value
    });
  };
  const startEditingShop = (shop: Shop) => {
    setEditingShopId(shop.id);
    setEditName(shop.name);
    setEditCharges(shop.additionalCharges);
  };
  const saveShopChanges = () => {
    if (editingShopId === null) return;
    setShops(prevShops => prevShops.map(shop => shop.id === editingShopId ? {
      ...shop,
      name: editName,
      additionalCharges: editCharges
    } : shop));
    setEditingShopId(null);
  };
  const cancelEditing = () => {
    setEditingShopId(null);
  };
  const confirmDelete = (id: number) => {
    setShowDeleteConfirm(id);
  };
  const handleDeleteShop = () => {
    if (showDeleteConfirm !== null) {
      onDeleteShop(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };
  return <div className="space-y-6" data-id="element-153">
      <div data-id="element-154">
        <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-155">Settings</h2>
        <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-156">
          <h3 className="font-medium mb-3" data-id="element-157">Billing Settings</h3>
          <div className="space-y-4" data-id="element-158">
            <div data-id="element-159">
              <label htmlFor="costPerUnit" className="block text-sm text-gray-600 mb-1" data-id="element-160">
                Cost per Unit (₹)
              </label>
              <input id="costPerUnit" type="number" value={settings.costPerUnit} onChange={handleCostPerUnitChange} step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-161" />
            </div>
          </div>
        </div>
      </div>
      <div data-id="element-162">
        <div className="flex justify-between items-center mb-3" data-id="element-163">
          <h3 className="font-medium" data-id="element-164">Shop Settings</h3>
          <button onClick={onAddShop} className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700" data-id="element-165">
            <PlusIcon size={16} data-id="element-166" />
            <span data-id="element-167">Add Shop</span>
          </button>
        </div>
        <div className="space-y-3" data-id="element-168">
          {shops.map(shop => <div key={shop.id} className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-169">
              {editingShopId === shop.id ? <div className="space-y-3" data-id="element-170">
                  <div data-id="element-171">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-172">
                      Shop Name
                    </label>
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-173" />
                  </div>
                  <div data-id="element-174">
                    <label className="block text-sm text-gray-600 mb-1" data-id="element-175">
                      Additional Charges (₹)
                    </label>
                    <input type="number" value={editCharges} onChange={e => setEditCharges(parseFloat(e.target.value) || 0)} step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-176" />
                  </div>
                  <div className="flex space-x-2 pt-2" data-id="element-177">
                    <button onClick={saveShopChanges} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-178">
                      Save
                    </button>
                    <button onClick={cancelEditing} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-179">
                      Cancel
                    </button>
                  </div>
                </div> : <div className="flex justify-between items-center" data-id="element-180">
                  <div data-id="element-181">
                    <h4 className="font-medium" data-id="element-182">{shop.name}</h4>
                    <p className="text-sm text-gray-600" data-id="element-183">
                      Additional Charges: ₹{shop.additionalCharges.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex space-x-2" data-id="element-184">
                    <button onClick={() => startEditingShop(shop)} className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-185">
                      Edit
                    </button>
                    <button onClick={() => confirmDelete(shop.id)} className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded-md hover:bg-red-50" data-id="element-186">
                      <TrashIcon size={16} data-id="element-187" />
                    </button>
                  </div>
                </div>}
            </div>)}
        </div>
        {shops.length === 0 && <div className="bg-white p-6 rounded-lg border border-gray-200 text-center" data-id="element-188">
            <p className="text-gray-600" data-id="element-189">No shops available</p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-190">
              Add a shop to get started
            </p>
          </div>}
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm !== null && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-191">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm" data-id="element-192">
            <h3 className="text-lg font-semibold mb-2" data-id="element-193">Confirm Delete</h3>
            <p className="text-gray-600 mb-4" data-id="element-194">
              Are you sure you want to delete this shop? This action cannot be
              undone.
            </p>
            <div className="flex space-x-3" data-id="element-195">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-196">
                Cancel
              </button>
              <button onClick={handleDeleteShop} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-197">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};